import type { Report } from '@/services/admin/reports.service'
import type { ReportMeta } from '@/components/super-admin/reports/reports-meta'

/** Formata data ISO → DD/MM/AAAA (pt-BR); vazio se ausente. */
function formatBrDate(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('pt-BR')
}

/** Escapa um campo pro CSV (aspas + separador + quebra de linha). */
export function csvField(value: string | number | null | undefined): string {
  const s = value === null || value === undefined ? '' : String(value)
  if (/[";\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Gera o CSV "achatado" a partir do relatório. Colunas variam por tipo:
 * - registered: 1 linha por usuário.
 * - em andamento: 1 linha por (usuário × curso) com % concluído + última atividade.
 * - concluído: 1 linha por (usuário × curso) com data de conclusão.
 * Usa `;` como separador (padrão pt-BR do Excel).
 */
export function buildReportCsv(report: Report, meta: ReportMeta): string {
  const lines: string[] = []

  if (meta.kind === 'registered') {
    lines.push(['Nome', 'E-mail', 'Empresa', 'Data de cadastro'].map(csvField).join(';'))
    for (const row of report.rows) {
      lines.push(
        [row.name, row.email, row.companyName ?? '', formatBrDate(row.registeredAt)]
          .map(csvField)
          .join(';')
      )
    }
    return lines.join('\n')
  }

  if (meta.kind === 'inProgress') {
    lines.push(
      ['Nome', 'E-mail', 'Empresa', meta.contentLabel, '% concluído', 'Última atividade']
        .map(csvField)
        .join(';')
    )
    for (const row of report.rows) {
      const base = [row.name, row.email, row.companyName ?? '']
      for (const i of row.items) {
        lines.push(
          [...base, i.title, `${i.progressPercent ?? 0}%`, formatBrDate(i.lastActivityAt)]
            .map(csvField)
            .join(';')
        )
      }
    }
    return lines.join('\n')
  }

  // completed
  lines.push(
    ['Nome', 'E-mail', 'Empresa', meta.contentLabel, 'Data de conclusão'].map(csvField).join(';')
  )
  for (const row of report.rows) {
    const base = [row.name, row.email, row.companyName ?? '']
    for (const i of row.items) {
      lines.push([...base, i.title, formatBrDate(i.completedAt)].map(csvField).join(';'))
    }
  }
  return lines.join('\n')
}

/** Dispara o download do CSV no navegador (BOM pra Excel abrir com acento certo). */
export function downloadReportCsv(report: Report, meta: ReportMeta): void {
  const csv = buildReportCsv(report, meta)
  const BOM = String.fromCharCode(0xfeff) // Excel abre UTF-8 com acento correto
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `relatorio-${meta.type}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
